import { motion } from "framer-motion";
import { Building2, ExternalLink, BarChart3, Target, TrendingUp, Layers, Zap, Copy } from "lucide-react";
import { Layout } from "@/components/Layout";
import { copyToClipboard } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";
import { EXTERNAL_LINKS } from "@/lib/index";

export default function RoiPage() {
  const { t } = useLanguage();

  const SERVICES = [
    { icon: BarChart3, title: t("roi.service1Title"), desc: t("roi.service1Desc") },
    { icon: Target, title: t("roi.service2Title"), desc: t("roi.service2Desc") },
    { icon: TrendingUp, title: t("roi.service3Title"), desc: t("roi.service3Desc") },
    { icon: Layers, title: t("roi.service4Title"), desc: t("roi.service4Desc") },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-12">
        {/* ── Enhanced Hero Section ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="relative rounded-[2rem] overflow-hidden border border-border/40 group shadow-2xl"
          style={{ background: "oklch(0.10 0.03 235)" }}
        >
          {/* Hero Banner Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/roi_banner.jpg" 
              alt="The ROI Architect Banner" 
              className="w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          </div>

          {/* Content Over Banner */}
          <div className="relative z-10 p-8 sm:p-12 flex flex-col items-center text-center space-y-8">
            {/* Logo Badge */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-black/80 flex items-center justify-center ring-4 ring-primary/40 p-1 shadow-2xl"
            >
              <img src="/roi_logo.png" alt="The ROI Architect Logo" className="w-full h-full object-contain rounded-full" />
            </motion.div>

            <div className="space-y-4 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 text-primary text-xs font-bold tracking-widest uppercase border border-primary/30">
                <Zap className="w-3.5 h-3.5" />
                {t("roi.heroBadge")}
              </div>
              <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight">
                {t("roi.title")}
              </h1>
              <p className="text-lg sm:text-xl text-primary font-bold">
                {t("roi.subtitle")}
              </p>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-2xl mx-auto font-medium">
                {t("roi.description")}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <a
                href={EXTERNAL_LINKS.ROI_ARCHITECT}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-primary text-primary-foreground text-sm font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/25"
              >
                <Building2 className="w-5 h-5" />
                {t("common.followLinkedIn")}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* ── Services Section ── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.1 }}
          className="space-y-8"
        >
          <div className="flex flex-col items-center text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-black text-foreground">{t("roi.areas")}</h2>
            <div className="w-20 h-1.5 bg-primary rounded-full" />
          </div>
          
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {SERVICES.map((s, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                className="p-6 rounded-3xl border border-border/60 bg-card hover:border-primary/50 transition-all duration-300 group shadow-lg hover:shadow-primary/5"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-inner">
                    <s.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-base mb-2">{s.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* ── Philosophy Section ── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.2 }}
          className="relative rounded-[2.5rem] border border-accent/20 bg-accent/5 p-10 sm:p-16 space-y-8 text-center overflow-hidden"
        >
          {/* Decor */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-accent/10 rounded-full -ml-16 -mt-16 blur-2xl" />
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-primary/10 rounded-full -mr-16 -mb-16 blur-2xl" />

          <h2 className="text-2xl sm:text-4xl font-black text-foreground relative z-10">{t("roi.philosophy")}</h2>
          <div className="max-w-3xl mx-auto space-y-6 relative z-10 group">
            <blockquote className="italic text-xl sm:text-2xl text-muted-foreground font-serif leading-relaxed relative">
              {t("roi.philosophyQuote")}
              <Button
                size="icon"
                variant="ghost"
                className="absolute -top-4 -right-4 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                onClick={() => copyToClipboard(`${t("roi.philosophyQuote")} - The ROI Architect`)}
              >
                <Copy className="w-4 h-4 text-primary" />
              </Button>
            </blockquote>
            <div className="w-24 h-1 bg-accent/40 mx-auto rounded-full" />
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t("roi.philosophyDesc")}
            </p>
          </div>
          <a
            href={EXTERNAL_LINKS.ROI_ARCHITECT}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-sm font-black text-primary hover:text-primary/70 transition-colors uppercase tracking-widest relative z-10"
          >
            {t("roi.philosophyBtn")}
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.section>
      </div>
    </Layout>
  );
}
